import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function WhyAutomation() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              Automation
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              April 15, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            Why Business Automation Is the Smartest Investment You Can Make in 2025
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Learn why business automation powered by AI saves time, reduces costs and drives growth, and how EsyAI can automate your workflows today.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-2-v2.png" 
            alt="Business Automation Workflow" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            Every hour your team spends on repetitive manual tasks is an hour not spent on strategy, sales, or growth. Business automation, powered by AI, is rapidly changing the way UK businesses operate, scale, and compete.
          </p>
          
          <p>
            At EsyAI, we help businesses automate their workflows, reduce operational friction, and free their teams to focus on what matters most. Here's why automation isn't just better. It's essential.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What Is Business Automation?</h2>
          <p>
            Business automation means using technology to perform repetitive tasks automatically, without human intervention. This ranges from simple email sequences to complex AI-powered workflows that process data, update CRMs, generate reports, and communicate with customers, all without a single manual step.
          </p>

          <h2 className="text-2xl mt-12 mb-6">1. Save Hundreds of Hours Every Month</h2>
          <p>
            The most immediate benefit of automation is time. Tasks like data entry, appointment scheduling, invoice generation, lead follow-up, and report creation consume enormous amounts of staff time. Automated systems handle these in seconds, freeing your team for high-value activities.
          </p>
          <p>
            Businesses using AI automation typically save 15 to 20 hours per week per employee on repetitive tasks.
          </p>

          <h2 className="text-2xl mt-12 mb-6">2. Eliminate Human Error</h2>
          <p>
            Manual processes are prone to mistakes, a mistyped figure, a forgotten follow-up, a missed deadline. Automated systems execute the same task the same way, every time, with near-perfect accuracy. This is especially critical for financial processes, customer data, and compliance.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>99.9% accuracy in automated data processing</li>
            <li>No forgotten follow-ups or missed tasks</li>
            <li>Consistent customer experiences at every touchpoint</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">3. Scale Without Increasing Headcount</h2>
          <p>
            One of the biggest bottlenecks for growing businesses is the need to hire more staff as workload increases. Automation breaks this constraint. Whether you handle 100 or 10,000 customer interactions per month, your automated systems scale effortlessly, without extra payroll.
          </p>

          <h2 className="text-2xl mt-12 mb-6">4. Run Your Business 24/7</h2>
          <p>
            Automation never sleeps. Your automated systems can respond to customer enquiries, process orders, send follow-up emails, and update records at any hour of the day. This means better customer service and more business captured outside of working hours.
          </p>

          <h2 className="text-2xl mt-12 mb-6">5. Reduce Operational Costs</h2>
          <p>
            Replacing time-consuming manual workflows with automation directly reduces labour costs and operational overhead. Businesses that implement AI automation typically see a 30 to 40% reduction in operational costs within the first year.
          </p>

          <h2 className="text-2xl mt-12 mb-6">6. Faster Decision-Making with Real-Time Data</h2>
          <p>
            Automated systems can collect, process, and present data in real time. Instead of waiting for a weekly report compiled manually, your team has live dashboards and instant insights, enabling faster, smarter decisions.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What EsyAI Can Automate for Your Business</h2>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>CRM updates and lead management</li>
            <li>Customer onboarding and follow-up emails</li>
            <li>Invoice generation and payment reminders</li>
            <li>Social media scheduling and reporting</li>
            <li>AI chatbots for 24/7 customer support</li>
            <li>Data pipeline and analytics automation</li>
          </ul>

          <hr className="my-12 border-white/10" />

          <h2 className="text-2xl mb-6">The Bottom Line</h2>
          <p>
            Automation isn't about replacing people. It's about empowering them. By removing the burden of repetitive tasks, your team can focus on creativity, relationships, and growth. Businesses that embrace automation today will be the ones leading their industries tomorrow.
          </p>

          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Ready to automate your workflows?</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              Book a free discovery call with EsyAI to find out what we can automate for you.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Get Started <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
